import { useEffect } from "react";

import { css } from "@compiled/react";
import { MessageDescriptor } from "react-intl";

import { ufoEventRedirectToProduct } from "@/components/signup/utils";

import { provisioningMessagesI18n } from "../../provisioning.messages.ts";
import { useSignupProvisioningContext } from "../../signup-provisioning-context";

import { ProgressCircle } from "./progress-circle";

const ProgressWrapper = css({
    alignItems: "center",
    display: "flex",
    width: "100%",
    justifyContent: "center"
});

const ProgressContainerStyle = css({
    fontSize: "14px",
    maxWidth: "438px",
    textAlign: "center",
    width: "100%"
});

export interface ProgressContainerProps {
    timeoutText?: MessageDescriptor;
    inProgressText?: MessageDescriptor;
    errorPollingText?: MessageDescriptor;
}

export const ProgressCircleContainer = ({
    timeoutText,
    inProgressText,
    errorPollingText
}: ProgressContainerProps) => {
    const { startTime, status } = useSignupProvisioningContext();
    const timeoutTextToUse =
        timeoutText ??
        provisioningMessagesI18n["SignupProvisioning.timeoutText.default"];
    const inProgressTextToUse =
        inProgressText ??
        provisioningMessagesI18n["SignupProvisioning.inProgressText.default"];
    const errorPollingTextToUse =
        errorPollingText ??
        provisioningMessagesI18n["SignupProvisioning.errorPollingText.default"];

    /**
     * UFOs for Signup's FE Observability & "Product Landing" SLOs
     *   - https://go/signup-ufo
     *   - https://go/signup-slo
     **/
    useEffect(() => {
        if (status === "error") {
            // SLO "bad event" for redirect into product
            ufoEventRedirectToProduct.failure({
                customData: {
                    reactComponent: "ProgressCircleContainer",
                    analyticsEvent: "errored",
                    provisioningStatus: status
                }
            });
        } else if (status === "timeout") {
            // SLO "bad event" for redirect into product
            ufoEventRedirectToProduct.failure({
                customData: {
                    reactComponent: "ProgressCircleContainer",
                    analyticsEvent: "timedOut",
                    provisioningStatus: status
                }
            });
        }
    }, [status]);

    return (
        <div css={ProgressWrapper} data-testid="signup-provisioning-module">
            <div css={ProgressContainerStyle}>
                <ProgressCircle
                    startTime={startTime}
                    message={
                        status === "error"
                            ? errorPollingTextToUse
                            : status === "timeout"
                            ? timeoutTextToUse
                            : inProgressTextToUse
                    }
                />
            </div>
        </div>
    );
};
